import { ICONS } from '@ui/icons/types';
import type { TranslateString } from '@/types/translation.types';
import type { EmailBlockContentV2 } from './widgets/block-types';
import { WidgetType } from './widgets/widget-registry';
import { templateStaticDefaults } from './template-defaults';
import {
  MCMXCIV_LOGO,
  MCMXCIV_LOGO_D4A847,
  DEFAULT_LOGO,
  BRANDTV_LOGO,
  CHATEAU_LOGO,
  LAURENT_LOGO,
  PINWHEEL_LOGO_DARK,
  PINWHEEL_LOGO_D6D5C9,
} from './utils/utils';
import { BLOCK_ID } from './widgets/block-ids';

const text = (base: string): TranslateString => ({ base, changed: '' });

const DEFAULT_SOCIAL_LINKS = [
  { platform: ICONS.FACEBOOK, href: '#' },
  { platform: ICONS.INSTAGRAM, href: '#' },
  { platform: ICONS.TWITTER, href: '#' },
];

/** remaining templates without an entry here keep rendering via their legacy fixed-JSX
 * fallback, by design (see default-blocks follow-up recipe)
 *
 * @param eventTitleFallback resolved real event/project title - used by templates (sterling,
 * astoria, siena) that have no static default title/salutation text of their own and fall back to
 * the real event name in their legacy branch (`getEventTitle(event, ...)`), as well as templates
 * (montclair, viceroy, carlton) whose default description is built from `descriptionTemplate`
 * rather than a static string; without this, those blocks would start empty instead of showing
 * the project name/reminder text. */
export function getDefaultBlocksForTemplate(
  templateId: string,
  eventTitleFallback?: string
): EmailBlockContentV2[] | undefined {
  if (templateId === 'capri') return buildCapriDefaultBlocks();
  if (templateId === 'sterling') return buildSterlingDefaultBlocks(eventTitleFallback);
  if (templateId === 'montclair') return buildMontclairDefaultBlocks(eventTitleFallback);
  if (templateId === 'amalfi') return buildAmalfiDefaultBlocks();
  if (templateId === 'astoria') return buildAstoriaDefaultBlocks(eventTitleFallback);
  if (templateId === 'florence') return buildFlorenceDefaultBlocks();
  if (templateId === 'chateau') return buildChateauDefaultBlocks();
  if (templateId === 'laurent') return buildLaurentDefaultBlocks();
  if (templateId === 'palais') return buildPalaisDefaultBlocks();
  if (templateId === 'aurelia') return buildAureliaDefaultBlocks();
  if (templateId === 'viceroy') return buildViceroyDefaultBlocks(eventTitleFallback);
  if (templateId === 'carlton') return buildCarltonDefaultBlocks(eventTitleFallback);
  if (templateId === 'siena') return buildSienaDefaultBlocks(eventTitleFallback);
  return undefined;
}

function buildCapriDefaultBlocks(): EmailBlockContentV2[] {
  const D = templateStaticDefaults.capri;

  return [
    { id: BLOCK_ID.salutation, type: WidgetType.Header, props: { value: text(D.salutation ?? '') } },
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    { id: BLOCK_ID.description, type: WidgetType.Paragraph, props: { value: text(D.description ?? '') } },
    { id: BLOCK_ID.dateTimeCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.legalText, type: WidgetType.Paragraph, props: { value: text(D.complianceText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildSterlingDefaultBlocks(eventTitleFallback?: string): EmailBlockContentV2[] {
  const D = templateStaticDefaults.sterling;

  return [
    { id: BLOCK_ID.headerLogo, type: WidgetType.Logo, props: { src: MCMXCIV_LOGO } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    { id: BLOCK_ID.salutation, type: WidgetType.Header, props: { value: text(D.salutation ?? '') } },
    // sterling has no static default title - it shows the real event name instead (see its
    // legacy branch's `getEventTitle(event, 'en')` fallback)
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? eventTitleFallback ?? '') } },
    { id: BLOCK_ID.description, type: WidgetType.Paragraph, props: { value: text(D.description ?? '') } },
    { id: BLOCK_ID.dateTimeCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.legalText, type: WidgetType.Paragraph, props: { value: text(D.complianceText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildMontclairDefaultBlocks(eventTitleFallback?: string): EmailBlockContentV2[] {
  const D = templateStaticDefaults.montclair;

  return [
    { id: BLOCK_ID.topBar, type: WidgetType.Spacer, props: { height: 20 } },
    { id: BLOCK_ID.salutation, type: WidgetType.Header, props: { value: text(D.salutation ?? '') } },
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    // montclair has no static default description - it's built from `descriptionTemplate`
    // using the real event title instead (see its legacy branch's `getEventTitle(event, ...)` fallback)
    {
      id: BLOCK_ID.description,
      type: WidgetType.Paragraph,
      props: { value: text(D.description ?? D.descriptionTemplate?.(eventTitleFallback ?? '') ?? '') },
    },
    { id: BLOCK_ID.dateTimeCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.divider, type: WidgetType.Divider, props: {} },
    { id: BLOCK_ID.social, type: WidgetType.Social, props: { links: DEFAULT_SOCIAL_LINKS } },
    { id: BLOCK_ID.legalText, type: WidgetType.Paragraph, props: { value: text(D.complianceText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildAmalfiDefaultBlocks(): EmailBlockContentV2[] {
  const D = templateStaticDefaults.amalfi;

  return [
    { id: BLOCK_ID.headerLogo, type: WidgetType.Logo, props: { src: PINWHEEL_LOGO_D6D5C9 } },
    { id: BLOCK_ID.salutation, type: WidgetType.Header, props: { value: text(D.salutation ?? '') } },
    { id: BLOCK_ID.description, type: WidgetType.Paragraph, props: { value: text(D.description ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    { id: BLOCK_ID.dateTimeCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.legalText, type: WidgetType.Paragraph, props: { value: text(D.complianceText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildAstoriaDefaultBlocks(eventTitleFallback?: string): EmailBlockContentV2[] {
  const D = templateStaticDefaults.astoria;

  return [
    { id: BLOCK_ID.headerLogo, type: WidgetType.Logo, props: { src: BRANDTV_LOGO } },
    { id: BLOCK_ID.salutation, type: WidgetType.Header, props: { value: text(D.salutation ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    // astoria has no static default title either - same real-event-name fallback as sterling
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? eventTitleFallback ?? '') } },
    { id: BLOCK_ID.description, type: WidgetType.Paragraph, props: { value: text(D.description ?? '') } },
    { id: BLOCK_ID.dateTimeCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.divider, type: WidgetType.Divider, props: {} },
    { id: BLOCK_ID.legalText, type: WidgetType.Paragraph, props: { value: text(D.complianceText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildFlorenceDefaultBlocks(): EmailBlockContentV2[] {
  const D = templateStaticDefaults.florence;

  return [
    { id: BLOCK_ID.topBar, type: WidgetType.Spacer, props: { height: 20 } },
    { id: BLOCK_ID.headerLogo, type: WidgetType.Logo, props: { src: BRANDTV_LOGO } },
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    { id: BLOCK_ID.dateTimeCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.description, type: WidgetType.Paragraph, props: { value: text(D.description ?? '') } },
    { id: BLOCK_ID.social, type: WidgetType.Social, props: { links: DEFAULT_SOCIAL_LINKS } },
    { id: BLOCK_ID.legalText, type: WidgetType.Paragraph, props: { value: text(D.complianceText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildChateauDefaultBlocks(): EmailBlockContentV2[] {
  const D = templateStaticDefaults.chateau;

  return [
    { id: BLOCK_ID.topBar, type: WidgetType.Spacer, props: { height: 4 } },
    { id: BLOCK_ID.headerLogo, type: WidgetType.Logo, props: { src: CHATEAU_LOGO } },
    { id: BLOCK_ID.countdownBadge, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    { id: BLOCK_ID.infoCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.social, type: WidgetType.Social, props: { links: DEFAULT_SOCIAL_LINKS } },
    { id: BLOCK_ID.tagline, type: WidgetType.Paragraph, props: { value: text(D.taglineText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildLaurentDefaultBlocks(): EmailBlockContentV2[] {
  const D = templateStaticDefaults.laurent;

  return [
    { id: BLOCK_ID.headerLogo, type: WidgetType.Logo, props: { src: LAURENT_LOGO } },
    { id: BLOCK_ID.accentBar, type: WidgetType.Spacer, props: { height: 4 } },
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    { id: BLOCK_ID.description, type: WidgetType.Paragraph, props: { value: text(D.description ?? '') } },
    { id: BLOCK_ID.dateTimeCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.divider, type: WidgetType.Divider, props: {} },
    { id: BLOCK_ID.social, type: WidgetType.Social, props: { links: DEFAULT_SOCIAL_LINKS } },
    // { id: BLOCK_ID.supportText, type: WidgetType.Paragraph, props: { value: text(D.supportText ?? '') } },
    { id: BLOCK_ID.legalText, type: WidgetType.Paragraph, props: { value: text(D.complianceText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildPalaisDefaultBlocks(): EmailBlockContentV2[] {
  const D = templateStaticDefaults.palais;

  return [
    { id: BLOCK_ID.topBar, type: WidgetType.Spacer, props: { height: 20 } },
    { id: BLOCK_ID.divider1, type: WidgetType.Divider, props: {} },
    { id: BLOCK_ID.salutation, type: WidgetType.Header, props: { value: text(D.salutation ?? '') } },
    { id: BLOCK_ID.divider2, type: WidgetType.Divider, props: {} },
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    { id: BLOCK_ID.infoCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.divider3, type: WidgetType.Divider, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.description, type: WidgetType.Paragraph, props: { value: text(D.description ?? '') } },
    { id: BLOCK_ID.divider4, type: WidgetType.Divider, props: {} },
    { id: BLOCK_ID.tagline, type: WidgetType.Paragraph, props: { value: text(D.taglineText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildAureliaDefaultBlocks(): EmailBlockContentV2[] {
  const D = templateStaticDefaults.aurelia;

  return [
    { id: BLOCK_ID.headerLogo, type: WidgetType.Logo, props: { src: PINWHEEL_LOGO_DARK } },
    { id: BLOCK_ID.badge, type: WidgetType.Header, props: { value: text(D.liveBadgeText ?? '') } },
    { id: BLOCK_ID.salutation, type: WidgetType.Header, props: { value: text(D.salutation ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    { id: BLOCK_ID.description, type: WidgetType.Paragraph, props: { value: text(D.description ?? '') } },
    { id: BLOCK_ID.dateTimeCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.legalText, type: WidgetType.Paragraph, props: { value: text(D.complianceText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildViceroyDefaultBlocks(eventTitleFallback?: string): EmailBlockContentV2[] {
  const D = templateStaticDefaults.viceroy;

  return [
    { id: BLOCK_ID.headerLogo, type: WidgetType.Logo, props: { src: DEFAULT_LOGO } },
    { id: BLOCK_ID.badge, type: WidgetType.Header, props: { value: text(D.liveBadgeText ?? '') } },
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    // viceroy has no static default description either - same descriptionTemplate fallback as montclair
    {
      id: BLOCK_ID.description,
      type: WidgetType.Paragraph,
      props: { value: text(D.description ?? D.descriptionTemplate?.(eventTitleFallback ?? '') ?? '') },
    },
    { id: BLOCK_ID.dateTimeCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.legalText, type: WidgetType.Paragraph, props: { value: text(D.complianceText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildCarltonDefaultBlocks(eventTitleFallback?: string): EmailBlockContentV2[] {
  const D = templateStaticDefaults.carlton;

  return [
    {
      id: BLOCK_ID.badge,
      type: WidgetType.Header,
      props: { value: text(D.watchOnDemandBadgeText ?? '') },
    },
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? '') } },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    { id: BLOCK_ID.salutation, type: WidgetType.Header, props: { value: text(D.salutation ?? '') } },
    // carlton has no static default description either - built from `descriptionTemplate`
    // (recordingAvailableTemplate) using the real event title
    {
      id: BLOCK_ID.description,
      type: WidgetType.Paragraph,
      props: { value: text(D.description ?? D.descriptionTemplate?.(eventTitleFallback ?? '') ?? '') },
    },
    { id: BLOCK_ID.infoCard, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.tagline, type: WidgetType.Header, props: { value: text(D.taglineText ?? '') } },
    { id: BLOCK_ID.communityText, type: WidgetType.Paragraph, props: { value: text(D.communityText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}

function buildSienaDefaultBlocks(eventTitleFallback?: string): EmailBlockContentV2[] {
  const D = templateStaticDefaults.siena;

  return [
    { id: BLOCK_ID.headerLogo, type: WidgetType.Logo, props: { src: MCMXCIV_LOGO_D4A847 } },
    { id: BLOCK_ID.title, type: WidgetType.Header, props: { value: text(D.title ?? '') } },
    // siena has no static default salutation - it shows the real event/project name instead
    // (see its legacy branch's `getEventTitle(event, 'en')` fallback)
    {
      id: BLOCK_ID.salutation,
      type: WidgetType.Header,
      props: { value: text(D.salutation ?? eventTitleFallback ?? '') },
    },
    { id: BLOCK_ID.mainImage, type: WidgetType.Image, props: { src: D.image ?? '' } },
    { id: BLOCK_ID.infoList, type: WidgetType.DateTimeCard, props: {} },
    { id: BLOCK_ID.cta, type: WidgetType.Button, props: { value: text(D.cta ?? '') } },
    { id: BLOCK_ID.description, type: WidgetType.Paragraph, props: { value: text(D.description ?? '') } },
    { id: BLOCK_ID.divider, type: WidgetType.Divider, props: {} },
    { id: BLOCK_ID.tagline, type: WidgetType.Paragraph, props: { value: text(D.taglineText ?? '') } },
    {
      id: BLOCK_ID.mailingAddress,
      type: WidgetType.Footer,
      props: { value: text(D.footerText ?? ''), src: '' },
    },
  ];
}
